import { create } from 'zustand';
import type { AdjustParams, CurvePoint } from '../core/filters';
import { clampParams } from '../core/filters';
import { History, type HistoryRow } from '../core/history/history';
import { HISTORY_LIMIT, HISTORY_MEMORY_BUDGET } from '../core/limits';
import { createDocument, estimateDocumentBytes, marginalLayerBytes } from '../core/model/document';
import { findLayer, leafLayers, rowOrder } from '../core/model/tree';
import type { AdjustmentKind, EditorDocument, Layer, Rect, Size, Vec2 } from '../core/model/types';
import type { LayerTransform } from '../core/move';
import type { Guide } from '../core/selection';
import type { ThemePreference } from '../core/theme';
import { isThemePreference } from '../core/theme';
import type { ToolId } from '../core/tools';
import {
  actualSizeViewport,
  clampZoom,
  fitViewport,
  type Viewport,
  zoomAround,
} from '../core/view';
import { readPreference, writePreference } from '../io/preferences';

export type RightPanelId = 'layers' | 'adjust' | 'document' | 'history';
export type ToastKind = 'info' | 'warn' | 'error';
/** Which resize dialog is open, if any. */
export type ResizeDialogMode = null | 'image' | 'canvas';

/** A layer's provisional transform during a move gesture (see `layerPreview`). */
export type LayerPreview = { layerId: string } & LayerTransform;

/** Painting settings. Values are document pixels and 0..1 fractions. */
export interface PaintOptions {
  size: number;
  hardness: number;
  opacity: number;
  smoothing: number;
}

/** Options for the shape tools (line, rectangle, ellipse, arrow). */
export interface ShapeOptions {
  /** Stroke width in document pixels. */
  width: number;
  /** Fill closed shapes instead of outlining them. */
  filled: boolean;
}

/**
 * A live adjustment being previewed on a layer. The heavy part — the decoded
 * pixel buffer — lives in `app/adjust.ts`; this is only what the UI needs to
 * render, so the store stays cheap to copy and easy to test.
 */
export interface AdjustmentSession {
  layerId: string;
  kind: AdjustmentKind;
  /** Sliders and/or curve, depending on what the kind offers. */
  params: AdjustParams;
  /**
   * False while the layer is still being decoded. The panel switches over
   * immediately so a click is never silently swallowed, but a preview cannot be
   * produced until the pixels are in memory — and anything that waits on the
   * preview (including tests) must wait on this, not on the panel.
   */
  ready: boolean;
}

/** Options for the text tool. */
export interface TextOptions {
  fontSize: number;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
}

/**
 * A text edit in progress. It lives in the store rather than in a component so
 * that the tool, the overlay and the commit path all see one source of truth,
 * and so a draft survives a re-render of the stage.
 */
export interface TextDraft {
  /** Existing layer being edited, or null when this draft will become a layer. */
  layerId: string | null;
  x: number;
  y: number;
  text: string;
  fontSize: number;
  fontFamily: string;
  bold: boolean;
  italic: boolean;
  color: string;
  /** In font sizes — the model's leading, which the overlay draws with. */
  lineHeight: number;
}

export interface Toast {
  id: number;
  message: string;
  kind: ToastKind;
}

/** A modal yes/no question raised by a command (e.g. "resize this oversized image?"). */
export interface ConfirmRequest {
  id: number;
  title: string;
  message: string;
  confirmLabel: string;
  cancelLabel: string;
}

export interface EditorState {
  /** The document. Always present: the app opens with a blank canvas. */
  doc: EditorDocument;
  viewport: Viewport;
  /** CSS-pixel size of the drawing area, reported by the engine. */
  hostSize: Size;
  activeTool: ToolId;
  rightPanel: RightPanelId;
  /**
   * Whether the right panel is on screen at all (Iteration 13).
   *
   * Tab choice and visibility are independent concerns and stay that way: the
   * tab is ephemeral, the visibility choice persists like the theme.
   */
  rightPanelOpen: boolean;
  /** Chosen theme, not the resolved one: 'system' keeps following the OS. */
  theme: ThemePreference;
  /** A newer deployed version is waiting to take over. */
  updateAvailable: boolean;
  /** Browser online flag, mirrored for the status bar. */
  online: boolean;
  showGrid: boolean;
  showShortcuts: boolean;
  showAbout: boolean;
  /** Command palette (Ctrl/Cmd+K). Ephemeral UI, never persisted. */
  paletteOpen: boolean;
  /** Pointer position in document coordinates, for the status bar. */
  pointer: Vec2 | null;
  /** id of the mounted render engine, or null before the stage mounts. */
  engineId: string | null;
  toast: Toast | null;
  confirm: ConfirmRequest | null;
  exportDialogOpen: boolean;
  /** Batch-export dialog (File → Batch Export…). Ephemeral UI, never persisted. */
  batchExportOpen: boolean;
  /** Marquee in document pixels, or null when nothing is selected. */
  selection: Rect | null;
  /**
   * A layer's provisional transform while a move gesture is in progress.
   *
   * Lives outside the document on purpose — same rule as the adjustment
   * preview: a drag is not a fact until it is released, so history and the
   * model stay untouched until then.
   */
  layerPreview: LayerPreview | null;
  /**
   * Every member's provisional transform during a **joint** drag — the
   * selection set or a group moving, scaling or rotating as one (Iteration
   * 15). The singular preview stays the primary's; this is the drag's full
   * truth, and what the overlay's union box is drawn from. Cleared everywhere
   * the singular is, so the two can never disagree about whether a gesture is
   * in flight.
   */
  layerPreviews: LayerPreview[] | null;
  /**
   * What the pointer is over while the move tool is active: a handle id (or
   * 'rotate'/'move'), which the stage turns into a cursor. The transform
   * overlay is pointer-events-none, so the hook that owns hit-testing also
   * owns the cursor — one source of truth, no DOM fighting.
   */
  moveHover: string | null;
  guides: Guide[];
  showRulers: boolean;
  resizeDialog: ResizeDialogMode;
  /** Layer the panel and layer commands act on — the primary of the selection set. */
  activeLayerId: string | null;
  /**
   * The multi-selection: every layer the panel highlights and the set-aware
   * commands act on (Iteration 12 — docs/DESIGN-iteration-12.md).
   *
   * Runtime state, like the marquee and the move preview: never persisted, so
   * the project format is untouched. The invariant the store repairs after
   * every change: the active layer is a member of the set, or both are null —
   * the primary owns the handles, the right panel and the mask flows, and a
   * set that forgot its primary would be a highlight with no meaning.
   */
  selectedIds: ReadonlySet<string>;
  /** Where a Shift-click range grows from — the last row clicked without Shift. */
  selectionAnchor: string | null;
  /**
   * Layer whose **mask** painting targets, or null when painting hits pixels.
   *
   * Only the layer id is stored: the mask itself is in the document, so undo
   * naturally drops out of mask editing when the mask is removed.
   */
  maskEditing: string | null;
  /** Brush/pencil/eraser settings, shared across the painting tools. */
  paint: PaintOptions;
  shape: ShapeOptions;
  text: TextOptions;
  /** Non-null while an adjustment is being previewed on a layer. */
  adjustment: AdjustmentSession | null;
  /** Non-null while the text tool is editing on the canvas. */
  textDraft: TextDraft | null;
  /** 0..100, how far a flood fill may stray from the clicked colour. */
  fillTolerance: number;
  /** Foreground colour. */
  color: string;
  recentColors: string[];

  canUndo: boolean;
  canRedo: boolean;
  historyRows: HistoryRow[];
  /** Approximate bytes the undo stack is holding, for the History panel. */
  historyBytes: number;
  /** Bumped on every document change so cheap selectors can re-render panels. */
  revision: number;

  /** Replace the document and record a history entry. */
  commit(label: string, doc: EditorDocument): void;
  /** Functional form of {@link commit}. */
  updateDoc(label: string, updater: (doc: EditorDocument) => EditorDocument): void;
  /** Start a new document (resets history). */
  newDocument(width: number, height: number, background?: string): void;
  loadDocument(doc: EditorDocument): void;

  setViewport(viewport: Viewport): void;
  setHostSize(size: Size): void;
  zoomBy(factor: number, anchor?: Vec2): void;
  zoomTo(zoom: number, anchor?: Vec2): void;
  zoomToFit(): void;
  zoomToActualSize(): void;

  setTool(tool: ToolId): void;
  setRightPanel(panel: RightPanelId): void;
  /** Reveal the panel, optionally switching tabs — the way commands open it. */
  openRightPanel(panel?: RightPanelId): void;
  /** Flip the panel's visibility and persist the choice. */
  toggleRightPanel(): void;
  setTheme(theme: ThemePreference): void;
  setUpdateAvailable(available: boolean): void;
  setOnline(online: boolean): void;
  toggleGrid(): void;
  setShowShortcuts(open: boolean): void;
  setShowAbout(open: boolean): void;
  setPaletteOpen(open: boolean): void;
  setPointer(point: Vec2 | null): void;
  setEngineId(id: string | null): void;

  notify(message: string, kind?: ToastKind): void;
  dismissToast(): void;
  /** Ask the user a yes/no question; resolves false when dismissed. */
  askConfirm(
    request: Omit<ConfirmRequest, 'id' | 'cancelLabel'> & { cancelLabel?: string },
  ): Promise<boolean>;
  respondConfirm(accepted: boolean): void;
  setExportDialog(open: boolean): void;
  setBatchExportDialog(open: boolean): void;
  setSelection(rect: Rect | null): void;
  setLayerPreview(preview: LayerPreview | null): void;
  /** The whole set's provisional transforms, or null when no joint drag is live. */
  setLayerPreviews(previews: LayerPreview[] | null): void;
  setMoveHover(handle: string | null): void;
  addGuide(guide: Guide): void;
  moveGuide(index: number, to: Guide): void;
  removeGuide(index: number): void;
  setShowRulers(value: boolean): void;
  setResizeDialog(mode: ResizeDialogMode): void;
  /** Select exactly this layer (or deselect), collapsing any multi-selection. */
  setActiveLayer(id: string | null): void;
  /** Ctrl/Cmd-click: add a layer to, or remove it from, the selection. */
  toggleSelected(id: string): void;
  /** Shift-click: select the rows between the anchor and `toId`, as drawn. */
  selectRange(fromId: string, toId: string): void;
  /**
   * Assign a whole selection — what the set-aware commands do after they run.
   * Named `assignSelection`, not `setSelection`: the marquee owns that name.
   */
  assignSelection(ids: Iterable<string>, primaryId?: string | null): void;
  setMaskEditing(layerId: string | null): void;
  setPaint(patch: Partial<PaintOptions>): void;
  setShape(patch: Partial<ShapeOptions>): void;
  setText(patch: Partial<TextOptions>): void;
  setAdjustment(session: AdjustmentSession | null): void;
  /** Set one slider, in that control's stored units. */
  setAdjustmentParam(key: string, value: number): void;
  /** Replace the tone curve of a curve-kind session. */
  setAdjustmentCurve(points: CurvePoint[]): void;
  setTextDraft(draft: TextDraft | null): void;
  setFillTolerance(value: number): void;
  setColor(color: string): void;
  /** Move the active layer through the stack; 1 goes towards the top. */
  selectAdjacentLayer(direction: 1 | -1): void;

  undo(): boolean;
  redo(): boolean;
}

export const DEFAULT_DOC_SIZE = { width: 800, height: 600 } as const;

export interface EditorStoreOptions {
  /** Override the history byte budget (tests, and a device that knows better). */
  historyMemoryBudget?: number;
}

/**
 * Factory so tests can build isolated stores; the app uses the singleton below.
 */
export function createEditorStore(initialDoc?: EditorDocument, options: EditorStoreOptions = {}) {
  const startDoc = initialDoc ?? createDocument(DEFAULT_DOC_SIZE);

  return create<EditorState>()((set, get) => {
    const history = new History<EditorDocument>(startDoc, {
      limit: HISTORY_LIMIT,
      memoryBudget: options.historyMemoryBudget ?? HISTORY_MEMORY_BUDGET,
      bytes: estimateDocumentBytes(startDoc),
    });
    let toastId = 0;
    let confirmId = 0;
    let confirmResolver: ((accepted: boolean) => void) | undefined;
    let toastTimer: ReturnType<typeof setTimeout> | undefined;

    const historyView = () => ({
      canUndo: history.canUndo,
      canRedo: history.canRedo,
      historyRows: history.rows,
      historyBytes: history.retainedBytes,
    });

    /**
     * Repair the whole selection — set, primary and anchor — after the
     * document changed under it (commit, undo, redo, load).
     *
     * The single-id guarantee this replaces was: deleting or merging a layer
     * must never leave the panel selecting a ghost, and the fallback is the
     * topmost leaf. The set gets the same discipline, one level up. The stack
     * is a tree, so "exists" means "is somewhere in the tree" — a layer
     * inside a group stays selected when the group is renamed.
     *
     * Members that no longer exist drop out; the primary falls to another
     * surviving member, in panel order, topmost first; an empty set falls to
     * the same topmost-leaf fallback the single id always had; and the primary
     * is always added back into the set, so the invariant holds by
     * construction and no command has to remember it.
     */
    const selectionFor = (
      doc: EditorDocument,
      ids: ReadonlySet<string>,
      activeId: string | null,
    ): { ids: ReadonlySet<string>; primary: string | null; anchor: string | null } => {
      const alive = new Set<string>();
      for (const id of ids) {
        if (findLayer(doc.layers, id)) alive.add(id);
      }
      let primary = activeId && alive.has(activeId) ? activeId : null;
      if (!primary) {
        const rows = rowOrder(doc.layers);
        primary = rows.find((id) => alive.has(id)) ?? null;
      }
      if (!primary) {
        const leaves = leafLayers(doc.layers);
        primary = leaves.length > 0 ? (leaves[leaves.length - 1] as Layer).id : null;
      }
      if (primary) alive.add(primary);
      return { ids: alive, primary, anchor: primary };
    };

    /**
     * The spread-able selection repair for a `set` call. With `fresh`, the
     * caller replaced the document wholesale (new, load, import): selection is
     * not persisted, so the set starts empty and the primary falls to the
     * topmost leaf, exactly as a first visit does.
     */
    const repairSelection = (
      state: { selectedIds: ReadonlySet<string>; activeLayerId: string | null },
      doc: EditorDocument,
      options: { fresh?: boolean } = {},
    ): {
      activeLayerId: string | null;
      selectedIds: ReadonlySet<string>;
      selectionAnchor: string | null;
    } => {
      const repaired = selectionFor(
        doc,
        options.fresh ? new Set<string>() : state.selectedIds,
        options.fresh ? null : state.activeLayerId,
      );
      return {
        activeLayerId: repaired.primary,
        selectedIds: repaired.ids,
        selectionAnchor: repaired.anchor,
      };
    };

    /**
     * Leave mask editing when the mask it pointed at is gone — removed, or
     * undone away. Painting must never silently target a mask that no longer
     * exists, because the next stroke would land on the layer's pixels instead.
     */
    const maskEditingFor = (doc: EditorDocument, current: string | null): string | null => {
      if (!current) return null;
      const layer = findLayer(doc.layers, current);
      return layer?.mask ? current : null;
    };

    const scheduleToastDismiss = () => {
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(() => set({ toast: null }), 4000);
    };

    return {
      doc: startDoc,
      viewport: { zoom: 1, pan: { x: 0, y: 0 } },
      hostSize: { width: 1, height: 1 },
      activeTool: 'move',
      rightPanel: 'document' as RightPanelId,
      rightPanelOpen: initialRightPanelOpen(),
      theme: initialTheme(),
      updateAvailable: false,
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      showGrid: false,
      showShortcuts: false,
      showAbout: false,
      paletteOpen: false,
      pointer: null,
      engineId: null,
      toast: null,
      confirm: null,
      exportDialogOpen: false,
      batchExportOpen: false,
      selection: null,
      layerPreview: null,
      layerPreviews: null,
      moveHover: null,
      guides: [],
      showRulers: false,
      resizeDialog: null,
      activeLayerId: null,
      selectedIds: new Set<string>(),
      selectionAnchor: null,
      paint: { size: 12, hardness: 0.8, opacity: 1, smoothing: 0.45 },
      shape: { width: 4, filled: false },
      text: {
        fontSize: 32,
        fontFamily: 'Inter, system-ui, sans-serif',
        bold: false,
        italic: false,
      },
      textDraft: null,
      adjustment: null,
      maskEditing: null,
      fillTolerance: 12,
      color: '#1f8bff',
      recentColors: ['#1f8bff', '#000000', '#ffffff'],
      revision: 0,
      ...historyView(),

      commit(label, doc) {
        // Charge the snapshot only for the layers this commit created. Every
        // untouched layer is the same object as in the snapshot before it, so
        // charging the whole document would multiply one file's size by the
        // length of the history and evict undo steps nobody asked to lose.
        history.push(doc, label, marginalLayerBytes(get().doc, doc));
        set((state) => ({
          doc,
          revision: state.revision + 1,
          ...repairSelection(state, doc),
          maskEditing: maskEditingFor(doc, state.maskEditing),
          ...historyView(),
        }));
      },

      updateDoc(label, updater) {
        get().commit(label, updater(get().doc));
      },

      newDocument(width, height, background) {
        const doc = createDocument({ width, height, background });
        history.clear(doc, estimateDocumentBytes(doc));
        set((state) => ({
          doc,
          revision: state.revision + 1,
          pointer: null,
          selection: null,
          layerPreview: null,
          layerPreviews: null,
          guides: [],
          maskEditing: null,
          ...repairSelection(state, doc, { fresh: true }),
          ...historyView(),
        }));
      },

      loadDocument(doc) {
        history.clear(doc, estimateDocumentBytes(doc));
        set((state) => ({
          doc,
          revision: state.revision + 1,
          selection: null,
          layerPreview: null,
          layerPreviews: null,
          maskEditing: null,
          ...repairSelection(state, doc, { fresh: true }),
          ...historyView(),
        }));
      },

      setViewport(viewport) {
        set({ viewport });
      },

      setHostSize(size) {
        const previous = get().hostSize;
        if (previous.width === size.width && previous.height === size.height) return;
        set({ hostSize: size });
      },

      zoomBy(factor, anchor) {
        const { viewport, hostSize } = get();
        const point = anchor ?? { x: hostSize.width / 2, y: hostSize.height / 2 };
        set({ viewport: zoomAround(viewport, factor, point) });
      },

      zoomTo(zoom, anchor) {
        const { viewport, hostSize } = get();
        const point = anchor ?? { x: hostSize.width / 2, y: hostSize.height / 2 };
        const factor = clampZoom(zoom) / viewport.zoom;
        set({ viewport: zoomAround(viewport, factor, point) });
      },

      zoomToFit() {
        const { doc, hostSize } = get();
        set({ viewport: fitViewport({ width: doc.width, height: doc.height }, hostSize) });
      },

      zoomToActualSize() {
        const { doc, hostSize } = get();
        set({ viewport: actualSizeViewport({ width: doc.width, height: doc.height }, hostSize) });
      },

      setTool(tool) {
        set({ activeTool: tool });
      },

      setRightPanel(panel) {
        set({ rightPanel: panel });
      },

      openRightPanel(panel) {
        // Tab-only changes are not persisted; revealing is. A second call with
        // the panel already open on the same tab changes nothing.
        const state = get();
        const patch: Partial<Pick<EditorState, 'rightPanel' | 'rightPanelOpen'>> = {};
        if (!state.rightPanelOpen) {
          patch.rightPanelOpen = true;
          writePreference('rightPanelOpen', '1');
        }
        if (panel !== undefined && panel !== state.rightPanel) patch.rightPanel = panel;
        if (patch.rightPanelOpen !== undefined || patch.rightPanel !== undefined) set(patch);
      },

      toggleRightPanel() {
        set((state) => {
          const open = !state.rightPanelOpen;
          writePreference('rightPanelOpen', open ? '1' : '0');
          return { rightPanelOpen: open };
        });
      },

      setTheme(theme) {
        writePreference('theme', theme);
        set({ theme });
      },

      setUpdateAvailable(available) {
        set({ updateAvailable: available });
      },

      setOnline(online) {
        set({ online });
      },

      toggleGrid() {
        set((state) => ({ showGrid: !state.showGrid }));
      },

      setShowShortcuts(open) {
        set({ showShortcuts: open });
      },

      setShowAbout(open) {
        set({ showAbout: open });
      },

      setPaletteOpen(open) {
        set({ paletteOpen: open });
      },

      setPointer(point) {
        const current = get().pointer;
        if (point === null && current === null) return;
        if (
          point &&
          current &&
          Math.round(point.x) === Math.round(current.x) &&
          Math.round(point.y) === Math.round(current.y)
        ) {
          // Pointer events fire far more often than the rounded position changes;
          // skipping identical updates keeps the status bar from re-rendering.
          return;
        }
        set({ pointer: point });
      },

      setEngineId(id) {
        set({ engineId: id });
      },

      notify(message, kind = 'info') {
        toastId += 1;
        set({ toast: { id: toastId, message, kind } });
        scheduleToastDismiss();
      },

      dismissToast() {
        if (toastTimer) clearTimeout(toastTimer);
        set({ toast: null });
      },

      askConfirm(request) {
        // A second question while one is open resolves the first as "no" rather
        // than stacking dialogs.
        confirmResolver?.(false);
        confirmId += 1;
        const full: ConfirmRequest = { id: confirmId, cancelLabel: 'Cancel', ...request };
        return new Promise<boolean>((resolve) => {
          confirmResolver = resolve;
          set({ confirm: full });
        });
      },

      respondConfirm(accepted) {
        confirmResolver?.(accepted);
        confirmResolver = undefined;
        set({ confirm: null });
      },

      setExportDialog(open) {
        set({ exportDialogOpen: open });
      },
      setBatchExportDialog(open) {
        set({ batchExportOpen: open });
      },
      setSelection(rect) {
        set({ selection: rect });
      },
      setLayerPreview(preview) {
        set({ layerPreview: preview });
      },
      setLayerPreviews(previews) {
        set({ layerPreviews: previews });
      },
      setMoveHover(handle) {
        set({ moveHover: handle });
      },
      addGuide(guide) {
        set((state) => {
          // Identical guides would be invisible and would collide as React keys.
          if (
            state.guides.some(
              (candidate) => candidate.axis === guide.axis && candidate.position === guide.position,
            )
          ) {
            return {};
          }
          return { guides: [...state.guides, guide] };
        });
      },
      moveGuide(index, to) {
        set((state) => ({
          guides: state.guides.map((guide, i) => (i === index ? to : guide)),
        }));
      },
      removeGuide(index) {
        set((state) => ({ guides: state.guides.filter((_, i) => i !== index) }));
      },
      setShowRulers(value) {
        set({ showRulers: value });
      },
      setResizeDialog(mode) {
        set({ resizeDialog: mode });
      },
      setActiveLayer(id) {
        // A plain click replaces the set: one layer selected, and it is the
        // anchor the next Shift-click grows from. Deselecting (null) empties
        // both — the next commit repairs them if it must.
        set({
          activeLayerId: id,
          selectedIds: id ? new Set([id]) : new Set<string>(),
          selectionAnchor: id,
        });
      },

      toggleSelected(id) {
        const state = get();
        const next = new Set(state.selectedIds);
        if (!next.has(id)) {
          next.add(id);
          set({ selectedIds: next, activeLayerId: id, selectionAnchor: id });
          return;
        }
        next.delete(id);
        if (next.size === 0) {
          // Toggling out the last member deselects, like clicking the canvas.
          set({ selectedIds: next, activeLayerId: null, selectionAnchor: id });
          return;
        }
        const repaired = selectionFor(
          state.doc,
          next,
          state.activeLayerId === id ? null : state.activeLayerId,
        );
        set({
          selectedIds: repaired.ids,
          activeLayerId: repaired.primary,
          // The anchor stays where the pointer last clicked, so a Shift-click
          // after a toggle grows from the row the user actually touched.
          selectionAnchor: id,
        });
      },

      selectRange(fromId, toId) {
        const { doc } = get();
        const rows = rowOrder(doc.layers);
        const from = rows.indexOf(fromId);
        const to = rows.indexOf(toId);
        if (from < 0 || to < 0) return;
        const [lo, hi] = from <= to ? [from, to] : [to, from];
        // The range replaces the set — the Photoshop rule. The clicked row is
        // the primary (it owns the handles), even when the old primary sits
        // inside the range; the anchor stays put so the next Shift-click
        // grows the same range from where it started.
        set({
          selectedIds: new Set(rows.slice(lo, hi + 1)),
          activeLayerId: toId,
          selectionAnchor: fromId,
        });
      },

      assignSelection(ids, primaryId) {
        const { doc } = get();
        const requested = [...ids];
        const preferred = primaryId !== undefined ? primaryId : (requested[0] ?? null);
        const repaired = selectionFor(doc, new Set(requested), preferred);
        // A command that assigns a selection believes in it: the preferred
        // primary wins when it exists in the tree, and only a stale one falls
        // back to the panel-order member — never the old primary.
        set({
          selectedIds: repaired.ids,
          activeLayerId: repaired.primary,
          selectionAnchor: repaired.anchor,
        });
      },
      setMaskEditing(layerId) {
        set({ maskEditing: layerId });
      },
      setPaint(patch) {
        set((state) => ({ paint: { ...state.paint, ...patch } }));
      },
      setShape(patch) {
        set((state) => ({ shape: { ...state.shape, ...patch } }));
      },
      setText(patch) {
        set((state) => ({ text: { ...state.text, ...patch } }));
      },
      setAdjustment(session) {
        set({ adjustment: session });
      },
      setAdjustmentParam(key, value) {
        set((state) =>
          state.adjustment
            ? {
                adjustment: {
                  ...state.adjustment,
                  // Clamped on the way in: a range input behaves itself, but the
                  // store is the wrong place to keep an out-of-range look that the
                  // filter then has to second-guess.
                  params: clampParams(state.adjustment.kind, {
                    ...state.adjustment.params,
                    sliders: { ...state.adjustment.params.sliders, [key]: value },
                  }),
                },
              }
            : {},
        );
      },
      setAdjustmentCurve(points) {
        set((state) =>
          state.adjustment
            ? {
                adjustment: {
                  ...state.adjustment,
                  params: { ...state.adjustment.params, curve: points },
                },
              }
            : {},
        );
      },
      setTextDraft(draft) {
        set({ textDraft: draft });
      },
      setFillTolerance(value) {
        set({ fillTolerance: Math.max(0, Math.min(100, value)) });
      },
      setColor(color) {
        set((state) => ({
          color,
          // Most recent first, no duplicates, capped so the strip stays a strip.
          recentColors: [
            color,
            ...state.recentColors.filter((candidate) => candidate !== color),
          ].slice(0, 12),
        }));
      },
      selectAdjacentLayer(direction) {
        const { doc, activeLayerId } = get();
        // The stack is a tree: walking it means walking the leaves, in the
        // order they are drawn.
        const leaves = leafLayers(doc.layers);
        if (leaves.length === 0) return;
        const index = leaves.findIndex((layer) => layer.id === activeLayerId);
        const next =
          index < 0
            ? leaves.length - 1
            : Math.max(0, Math.min(leaves.length - 1, index + direction));
        const layer = leaves[next];
        if (layer) {
          // Walking collapses the set to the row you landed on — navigation
          // means "one layer", the same rule a plain click follows.
          set({
            activeLayerId: layer.id,
            selectedIds: new Set([layer.id]),
            selectionAnchor: layer.id,
          });
        }
      },

      undo() {
        const doc = history.undo();
        if (doc === undefined) return false;
        set((state) => ({
          doc,
          revision: state.revision + 1,
          layerPreview: null,
          layerPreviews: null,
          // Undo can remove the active layer (undoing "New Layer"), and the
          // next stroke through ensurePaintTarget would then silently mint
          // another one — the same invariant commit repairs.
          ...repairSelection(state, doc),
          maskEditing: maskEditingFor(doc, state.maskEditing),
          ...historyView(),
        }));
        return true;
      },

      redo() {
        const doc = history.redo();
        if (doc === undefined) return false;
        set((state) => ({
          doc,
          revision: state.revision + 1,
          layerPreview: null,
          layerPreviews: null,
          ...repairSelection(state, doc),
          maskEditing: maskEditingFor(doc, state.maskEditing),
          ...historyView(),
        }));
        return true;
      },
    };
  });
}

/** The store the running app uses. */
export const useEditorStore = createEditorStore();

/**
 * Stored theme, or 'system' for a first visit. Read synchronously at store
 * creation so the first paint already has the right theme.
 */
function initialTheme(): ThemePreference {
  const stored = readPreference('theme');
  return isThemePreference(stored) ? stored : 'system';
}

/**
 * Stored sidebar choice, or open for a first visit. Read synchronously at
 * store creation so the first paint already has the layout the user chose —
 * a sidebar that flickers open after boot would be a layout jump.
 */
function initialRightPanelOpen(): boolean {
  return readPreference('rightPanelOpen') !== '0';
}

/** Escape hatch for commands and non-React code. */
export const editor = {
  get: () => useEditorStore.getState(),
  set: useEditorStore.setState,
  subscribe: useEditorStore.subscribe,
};
